// built-from: /knowledge-retrieval v1 impl:knowledge-retrieval role:implementation
// What a consultant can do with one facilitator reply (093): keep it as a page
// in the knowledge base, and open the pages the reply drew on. Rendered under
// each reply by the companion, through the one chat panel's footer parameter.
//
// Every API call goes through useKnowledgePages; this file only renders.
import { useCallback, useId, useState, type KeyboardEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useKnowledgePages } from '../hooks/useKnowledgePages';
import { pageTitleFromReply } from '../utils/knowledgePages';
import type { TurnFact } from '../utils/turnFacts';
import '../styles/FacilitatorReplyActions.css';

export interface ReplyCitation {
  path: string;
  title: string;
}

export interface FacilitatorReplyActionsProps {
  /** The reply text, saved verbatim as the page content. */
  reply: string;
  /** The conversation the reply came from, so the page points back at it. */
  sessionId: string;
  /** The pages this reply drew on, when it drew on any. */
  citations?: ReplyCitation[];
  // 100 (owner decision 7): what this reply captured, in the reply's own
  // footer. A long document lands many facts, so the line stays one line until
  // the consultant asks for the list.
  facts?: TurnFact[];
  /** True only where the attached screen can jump to a fact. */
  canHighlight?: boolean;
  /** Ask the screen to go to one fact. Supplied with `canHighlight`. */
  onHighlight?: (factId: string) => void;
}

const SAVE_FAILED = 'Could not save this page. Your text is kept here. Try again.';

/** Above this many, the list waits to be asked for. */
export const FACTS_SHOWN_UNASKED = 5;

function CapturedFacts({ facts, canHighlight, onHighlight }: {
  facts: TurnFact[];
  canHighlight: boolean;
  onHighlight?: (factId: string) => void;
}): JSX.Element {
  const listId = useId();
  const [open, setOpen] = useState(facts.length <= FACTS_SHOWN_UNASKED);
  const summary = facts.length === 1 ? 'Captured 1 fact' : `Captured ${facts.length} facts`;

  return (
    <div className="fra-facts">
      <button
        type="button"
        className="fra-facts-summary"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
      >
        {open
          ? <ChevronDown size={13} aria-hidden="true" />
          : <ChevronRight size={13} aria-hidden="true" />}
        <span>{summary}</span>
      </button>
      {open ? (
        <ul className="fra-facts-list" id={listId}>
          {facts.map((fact) => (
            <li key={fact.factId} className="fra-fact">
              {canHighlight && onHighlight ? (
                <button
                  type="button"
                  className="fra-fact-link"
                  onClick={() => onHighlight(fact.factId)}
                >
                  {fact.label}
                </button>
              ) : (
                <span className="fra-fact-text">{fact.label}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function FacilitatorReplyActions({
  reply,
  sessionId,
  citations = [],
  facts = [],
  canHighlight = false,
  onHighlight,
}: FacilitatorReplyActionsProps): JSX.Element {
  const { filePage } = useKnowledgePages();
  const titleFieldId = useId();
  const [naming, setNaming] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState<{ path: string; title: string } | null>(null);

  const startNaming = useCallback(() => {
    setTitle(pageTitleFromReply(reply));
    setNaming(true);
    setFailed(false);
  }, [reply]);

  const cancel = useCallback(() => {
    setNaming(false);
    setFailed(false);
  }, []);

  const save = useCallback(async () => {
    if (saving || !title.trim()) return;
    setSaving(true);
    setFailed(false);
    const result = await filePage({ title: title.trim(), content: reply, sessionId });
    setSaving(false);
    if (!result.ok) {
      setFailed(true);
      return;
    }
    setNaming(false);
    setSaved({ path: result.path, title: result.title });
  }, [filePage, reply, saving, sessionId, title]);

  const onTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void save();
    }
  };

  return (
    <div className="fra">
      {facts.length ? (
        <CapturedFacts facts={facts} canHighlight={canHighlight} onHighlight={onHighlight} />
      ) : null}

      {citations.length ? (
        <div className="fra-sources" role="group" aria-label="Sources">
          <span className="fra-sources-label">Sources</span>
          {citations.map((citation) => (
            <Link
              key={citation.path}
              className="fra-source"
              to={`/engagement/files/${citation.path}`}
            >
              {citation.title}
            </Link>
          ))}
        </div>
      ) : null}

      {saved ? (
        <p className="fra-saved" role="status">
          <span>Saved to the knowledge base</span>
          <Link className="fra-open" to={`/engagement/files/${saved.path}`}>Open</Link>
        </p>
      ) : naming ? (
        <div className="fra-namer">
          <label className="fra-label" htmlFor={titleFieldId}>Page title</label>
          <input
            id={titleFieldId}
            className="fra-input"
            type="text"
            value={title}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={onTitleKeyDown}
          />
          {failed ? <p className="fra-alert" role="alert">{SAVE_FAILED}</p> : null}
          <div className="fra-namer-actions">
            <button
              type="button"
              className="fra-button fra-button--primary"
              disabled={saving || !title.trim()}
              onClick={() => { void save(); }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="fra-button" onClick={cancel}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="fra-button fra-action" onClick={startNaming}>
          Save as page
        </button>
      )}
    </div>
  );
}

export default FacilitatorReplyActions;
